'use client';

import {
  Box,
  Button,
  Typography,
  Alert,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletIcon from '@mui/icons-material/Tablet';
import DeviceUnknownIcon from '@mui/icons-material/DeviceUnknown';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@microservices/design-system';
import type { ActiveSession } from '../types';

function getPlatformIcon(platform: string) {
  const lowerPlatform = platform.toLowerCase();
  if (lowerPlatform.includes('mobile') || lowerPlatform.includes('phone')) {
    return <PhoneAndroidIcon fontSize="small" />;
  }
  if (lowerPlatform.includes('tablet')) {
    return <TabletIcon fontSize="small" />;
  }
  if (
    lowerPlatform.includes('web') ||
    lowerPlatform.includes('desktop') ||
    lowerPlatform.includes('windows') ||
    lowerPlatform.includes('mac')
  ) {
    return <ComputerIcon fontSize="small" />;
  }
  return <DeviceUnknownIcon fontSize="small" />;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

interface SessionListProps {
  sessions: ActiveSession[];
  selectedSessions: Set<string>;
  isLoading: boolean;
  error: string | null;
  onToggleSelect: (sessionId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onTerminateSession: (sessionId: string) => void;
  onTerminateSelected: () => void;
  onTerminateAll: () => void;
  onContinue: () => void;
}

export function SessionList({
  sessions,
  selectedSessions,
  isLoading,
  error,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onTerminateSession,
  onTerminateSelected,
  onTerminateAll,
  onContinue,
}: SessionListProps) {
  const canContinue = sessions.length < 3; // Session limit is 3

  const handleSelectionChange = (next: Set<string>) => {
    if (next.size === 0) {
      onClearSelection();
      return;
    }
    const allIds = sessions.map((s) => s.sessionId);
    if (next.size === allIds.length && allIds.every((id) => next.has(id))) {
      onSelectAll();
      return;
    }
    for (const id of next) {
      if (!selectedSessions.has(id)) {
        onToggleSelect(id);
        return;
      }
    }
    for (const id of selectedSessions) {
      if (!next.has(id)) {
        onToggleSelect(id);
        return;
      }
    }
  };

  const columns: ColumnDef<ActiveSession, unknown>[] = [
    {
      id: 'device',
      header: 'Device',
      cell: ({ row }) => {
        const session = row.original;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>
              {getPlatformIcon(session.platform)}
            </Box>
            <Box minWidth={0}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {session.platform || 'Unknown Device'}
                </Typography>
                {session.rememberMe && (
                  <Chip label="Remember Me" size="small" variant="outlined" />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Created: {formatDate(session.createdAt)}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      accessorKey: 'lastIp',
      header: 'IP',
      cell: ({ row }) => (
        <Typography variant="caption" color="text.secondary">
          {row.original.lastIp}
        </Typography>
      ),
    },
    {
      id: 'lastActivity',
      header: 'Last active',
      cell: ({ row }) => (
        <Typography variant="caption" color="text.secondary">
          {getRelativeTime(row.original.lastActivity)}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {sessions.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            All sessions have been terminated.
          </Typography>
          <Button
            variant="contained"
            onClick={onContinue}
            disabled={isLoading}
            sx={{ mt: 2 }}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Continue to Login'}
          </Button>
        </Box>
      ) : (
        <>
          {/* Header with bulk actions */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="body2">
              {selectedSessions.size > 0
                ? `${selectedSessions.size} selected`
                : `${sessions.length} active session(s)`}
            </Typography>

            <Box display="flex" gap={1}>
              {selectedSessions.size > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={onTerminateSelected}
                  disabled={isLoading}
                >
                  Terminate Selected
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={onTerminateAll}
                disabled={isLoading}
              >
                Terminate All
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Sessions table */}
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            <DataTable
              data={sessions}
              columns={columns}
              getRowId={(session) => session.sessionId}
              selection={{ selected: selectedSessions, onChange: handleSelectionChange }}
              rowActions={(session) => (
                <IconButton
                  onClick={() => onTerminateSession(session.sessionId)}
                  disabled={isLoading}
                  color="error"
                  size="small"
                  title="Terminate session"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              density="compact"
              ariaLabel="Active sessions"
            />
          </Box>

          <Divider sx={{ mt: 2, mb: 2 }} />

          {/* Continue button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={onContinue}
              disabled={isLoading || !canContinue}
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : canContinue ? (
                'Continue'
              ) : (
                `Terminate at least ${sessions.length - 2} session(s) to continue`
              )}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
